export type TelegramThemeParams = Partial<{
  bg_color: string;
  text_color: string;
  hint_color: string;
  link_color: string;
  button_color: string;
  button_text_color: string;
  secondary_bg_color: string;
  header_bg_color: string;
  accent_text_color: string;
  section_bg_color: string;
  section_header_text_color: string;
  subtitle_text_color: string;
  destructive_text_color: string;
}>;

type TelegramWebApp = {
  initData: string;
  colorScheme?: "light" | "dark";
  themeParams?: TelegramThemeParams;
  isFullscreen?: boolean;
  safeAreaInset?: { top: number; right: number; bottom: number; left: number };
  contentSafeAreaInset?: { top: number; right: number; bottom: number; left: number };
  ready?: () => void;
  expand?: () => void;
  requestFullscreen?: () => void;
  setHeaderColor?: (
    color: "bg_color" | "secondary_bg_color" | `#${string}`
  ) => void;
  disableVerticalSwipes?: () => void;
  enableVerticalSwipes?: () => void;
  onEvent?: (event: string, handler: () => void) => void;
  offEvent?: (event: string, handler: () => void) => void;
  close?: () => void;
  openTelegramLink?: (url: string) => void;
  MainButton?: {
    setText: (text: string) => void;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    onClick: (handler: () => void) => void;
    offClick: (handler: () => void) => void;
  };
  BackButton?: {
    show: () => void;
    hide: () => void;
    onClick: (handler: () => void) => void;
    offClick: (handler: () => void) => void;
  };
};

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

const themeVariables: Record<keyof TelegramThemeParams, string> = {
  bg_color: "--tg-theme-bg-color",
  text_color: "--tg-theme-text-color",
  hint_color: "--tg-theme-hint-color",
  link_color: "--tg-theme-link-color",
  button_color: "--tg-theme-button-color",
  button_text_color: "--tg-theme-button-text-color",
  secondary_bg_color: "--tg-theme-secondary-bg-color",
  header_bg_color: "--tg-theme-header-bg-color",
  accent_text_color: "--tg-theme-accent-text-color",
  section_bg_color: "--tg-theme-section-bg-color",
  section_header_text_color: "--tg-theme-section-header-text-color",
  subtitle_text_color: "--tg-theme-subtitle-text-color",
  destructive_text_color: "--tg-theme-destructive-text-color"
};

export const applyTelegramTheme = (theme: TelegramThemeParams = {}) => {
  for (const [key, variable] of Object.entries(themeVariables)) {
    const value = theme[key as keyof TelegramThemeParams];
    if (value) document.documentElement.style.setProperty(variable, value);
  }
};

export const telegramWebApp = () => window.Telegram?.WebApp;

const FULLSCREEN_CONTROL_ROW_HEIGHT = 36;
const fullscreenControlInsetProperty = "--tg-fullscreen-controls-inset-top";

const syncFullscreenControlInset = (app?: TelegramWebApp) => {
  const safeTop = Math.max(0, Number(app?.safeAreaInset?.top) || 0);
  const contentTop = Math.max(0, Number(app?.contentSafeAreaInset?.top) || 0);
  const telegramCoveredHeight = Math.max(0, contentTop - safeTop);
  const extraTop = app?.isFullscreen
    ? Math.max(0, FULLSCREEN_CONTROL_ROW_HEIGHT - telegramCoveredHeight)
    : 0;
  document.documentElement.style.setProperty(
    fullscreenControlInsetProperty,
    `${extraTop}px`
  );
};

export const bindTelegramBackButton = (handler: () => void) => {
  const button = telegramWebApp()?.BackButton;
  button?.onClick(handler);
  button?.show();
  return () => {
    button?.offClick(handler);
    button?.hide();
  };
};

export const initializeTelegramWebApp = () => {
  const app = telegramWebApp();
  applyTelegramTheme(app?.themeParams);
  document.documentElement.dataset.theme = app?.colorScheme ?? "light";
  const handleTheme = () => {
    applyTelegramTheme(app?.themeParams);
    document.documentElement.dataset.theme = app?.colorScheme ?? "light";
  };
  const handleSafeArea = () => syncFullscreenControlInset(app);
  app?.onEvent?.("themeChanged", handleTheme);
  app?.onEvent?.("fullscreenChanged", handleSafeArea);
  app?.onEvent?.("fullscreenFailed", handleSafeArea);
  app?.onEvent?.("safeAreaChanged", handleSafeArea);
  app?.onEvent?.("contentSafeAreaChanged", handleSafeArea);
  syncFullscreenControlInset(app);
  app?.setHeaderColor?.("bg_color");
  app?.ready?.();
  app?.expand?.();
  if (!app?.isFullscreen) {
    try {
      app?.requestFullscreen?.();
    } catch {
      // Older Telegram clients can expose a partial API. The expanded layout
      // above remains the safe fallback and the Mini App must still load.
    }
  }
  return () => {
    app?.offEvent?.("themeChanged", handleTheme);
    app?.offEvent?.("fullscreenChanged", handleSafeArea);
    app?.offEvent?.("fullscreenFailed", handleSafeArea);
    app?.offEvent?.("safeAreaChanged", handleSafeArea);
    app?.offEvent?.("contentSafeAreaChanged", handleSafeArea);
  };
};

export const closeTelegramWebApp = () => telegramWebApp()?.close?.();

export const openTelegramLink = (url: string) => {
  const app = telegramWebApp();
  if (app?.openTelegramLink) {
    app.openTelegramLink(url);
    return;
  }
  window.location.assign(url);
};
